import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  IconButton,
  Modal,
  FormItem,
  Button,
  Typography,
} from '@getsynapse/design-system';
import MultiSelectDropdown from 'Organisms/MultiSelectDropdow/MultiSelectDropdown';
import intl from 'react-intl-universal';
import { FormOption } from 'utils/customTypes';
import { useDispatch, useSelector } from 'react-redux';
import {
  getDashboardViewers,
  clearDashboardViewers,
  shareDashboard,
  deleteViewer,
  dashboardViewersSelectors,
} from 'state/normalized/dashboard/dashboardViewersSlice';
import { SLICE_STATUS } from 'utils/constants';
import { dashboardSelectors } from 'state/normalized/dashboard/dashboardSlice';
import { RootState } from 'state/store';

interface ManageAccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  dashboardId: string;
}

type AccessListEntry = {
  id: string;
  initial: string;
  name: string;
  disabled: boolean;
  role: 'owner' | 'viewer';
};

const ManageAccessModal = ({
  isOpen,
  onClose,
  dashboardId,
}: ManageAccessModalProps) => {
  const [selectedViewers, setSelectedViewers] = useState<FormOption[]>([]);

  const dispatch = useDispatch();

  const dashboard = useSelector((state: RootState) =>
    dashboardSelectors.selectDashboardById(state, dashboardId)
  );
  const owners = useMemo(() => dashboard?.owners ?? [], [dashboard]);

  const { dashboardViewers, availableViewers } = useSelector(
    dashboardViewersSelectors.selectViewersByDashboardId(dashboardId)
  );
  const viewersStatus = useSelector(
    dashboardViewersSelectors.selectDashboardViewersStatus
  );

  const isLoadingViewers = viewersStatus === SLICE_STATUS.LOADING;

  const shareStatus = useSelector(dashboardViewersSelectors.selectShareStatus);
  const isSharing = shareStatus === SLICE_STATUS.LOADING;

  const deleteStatus = useSelector(
    dashboardViewersSelectors.selectDeleteStatus
  );
  const isDeleting = deleteStatus === SLICE_STATUS.LOADING;

  const viewerOptions = useMemo<FormOption[]>(
    () =>
      availableViewers.map((viewer) => ({
        label: `${viewer.firstName} ${viewer.lastName}`,
        value: viewer.id,
      })),
    [availableViewers]
  );

  const accessList = useMemo<AccessListEntry[]>(() => {
    const ownerEntries: AccessListEntry[] = owners.map((owner) => ({
      id: owner.id,
      initial: `${owner.firstName?.charAt(0) ?? ''}${
        owner.lastName?.charAt(0) ?? ''
      }`,
      name: `${owner.firstName ?? ''} ${owner.lastName ?? ''}`.trim(),
      disabled: owner.disabled ?? false,
      role: 'owner',
    }));

    const viewerEntries: AccessListEntry[] = dashboardViewers.map((viewer) => ({
      id: viewer.id,
      initial: `${viewer.firstName?.charAt(0) ?? ''}${
        viewer.lastName?.charAt(0) ?? ''
      }`,
      name: `${viewer.firstName ?? ''} ${viewer.lastName ?? ''}`.trim(),
      disabled: viewer.disabledAt !== null,
      role: 'viewer',
    }));

    return [...ownerEntries, ...viewerEntries];
  }, [owners, dashboardViewers]);

  useEffect(() => {
    if (isOpen) {
      dispatch(getDashboardViewers());
    }
  }, [isOpen, dispatch]);

  const handleInvite = useCallback(() => {
    if (selectedViewers.length === 0) return;
    dispatch(
      shareDashboard({
        dashboardId,
        userIds: selectedViewers.map((v) => v.value as string),
      })
    );
    setSelectedViewers([]);
  }, [dispatch, dashboardId, selectedViewers]);

  const handleDelete = useCallback(
    (viewerId: string) => {
      if (!viewerId) return;
      dispatch(deleteViewer({ dashboardId, viewerId }));
    },
    [dispatch, dashboardId]
  );

  const handleClose = useCallback(() => {
    setSelectedViewers([]);
    dispatch(clearDashboardViewers());
    onClose();
  }, [dispatch, onClose]);

  return (
    <Modal
      aria-label='Select Language'
      isOpen={isOpen}
      closeModal={handleClose}
      childrenClassName='pr-0'
      title={intl.get('MANAGE_ACCESS_MODAL.TITLE')}
      titleIcon={{
        name: 'share-social-outline',
        className: 'text-primary-dark',
      }}
      size='medium'
    >
      <div className='w-full flex flex-col gap-4'>
        <div className='flex items-end gap-2'>
          <FormItem
            className='p-0 flex-1'
            label={intl.get('MANAGE_ACCESS_MODAL.VIEWERS')}
          >
            <MultiSelectDropdown
              options={viewerOptions}
              values={selectedViewers}
              onChange={(options: FormOption[]) => setSelectedViewers(options)}
              triggerProps={{
                placeholder: intl.get('MANAGE_ACCESS_MODAL.SELECT_VIEWERS'),
              }}
              listProps={{ 'data-cy': 'viewers-options' }}
            />
          </FormItem>

          <Button
            variant='secondary'
            size='default'
            className='h-10'
            onClick={handleInvite}
            disabled={selectedViewers.length === 0 || isSharing}
            data-cy='invite_members_button'
          >
            {intl.get('MANAGE_ACCESS_MODAL.INVITE')}
          </Button>
        </div>

        <div className='flex flex-col gap-1'>
          <Typography
            variant='body2'
            className='font-semibold text-tiny text-neutral-black'
          >
            {intl.get('MANAGE_ACCESS_MODAL.WHO_CAN_ACCESS')}
          </Typography>

          {isLoadingViewers ? (
            <div className='flex items-center justify-between py-2 px-4 animate-pulse'>
              <div className='flex items-center gap-2'>
                <div className='w-8 h-8 rounded-full bg-neutral-lighter-two' />
                <div className='w-32 h-4 rounded bg-neutral-lighter-two' />
              </div>
              <div className='w-12 h-4 rounded bg-neutral-lighter-two' />
            </div>
          ) : (
            accessList.map((entry) => (
              <div
                key={entry.id}
                className='flex items-center justify-between py-2 px-4 gap-x-4'
              >
                <div className='flex items-center gap-2'>
                  <Avatar
                    imageSrc=''
                    initial={entry.initial}
                    name={entry.name}
                    disabled={entry.disabled}
                    deactivatedText={intl.get('DEACTIVATED')}
                  />
                  <Typography variant='body2'>{entry.name}</Typography>
                </div>
                <div className='flex items-center gap-4'>
                  <Typography
                    variant='body2'
                    className={
                      entry.role === 'owner' ? 'text-neutral-dark' : ''
                    }
                  >
                    {entry.role === 'owner'
                      ? intl.get('MANAGE_ACCESS_MODAL.OWNER')
                      : intl.get('MANAGE_ACCESS_MODAL.VIEWER')}
                  </Typography>
                  {entry.role === 'viewer' && (
                    <IconButton
                      name='trash'
                      onClick={() => handleDelete(entry.id)}
                      disabled={isDeleting}
                      className='text-base text-neutral-dark p-1'
                    />
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ManageAccessModal;
